<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { type CategoryFilter, EventStatus } from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import { CategorySorting } from "~/types/Category";

type Props = {
  searchTerm: string;
};
const props = defineProps<Props>();
const searchTerm = computed(() => props.searchTerm);

const { filtersApplied, filtersCleared } = useAnalytics();
const translateSortLabel = useTranslateSortLabel();
const router = useRouter();
const route = useRoute();

const sheetMenuIsOpen = ref(false);
const platformStore = usePlatformStore();
const platformId = computed(
  () => platformStore.getPlatformConfiguration.platformId,
);

const filtersQueryEnabled = computed(
  () => Boolean(searchTerm) && sheetMenuIsOpen.value,
);

const { data: filters, isLoading } = useQuery({
  ...queries.events.filters(platformId, searchTerm),
  staleTime: Infinity,
  enabled: filtersQueryEnabled,
  select: (filters) => {
    const categoryFilters = Object.fromEntries(
      Object.entries(filters.categoryFilters).map((entry) => {
        const [key, values] = entry as [string, CategoryFilter[]];

        return [
          key,
          values.map((value) => ({
            label: value.name,
            value: value.id,
          })),
        ];
      }),
    );

    return {
      ...filters,
      categoryFilters,
    };
  },
});

const getAppliedFilterTypes = () => {
  let appliedFilterTypes: string[] = [];

  const statusFilterType = route.query.eventStatus && "status";

  const filterCategories = filters.value?.categoryFilters;
  const routeCategories =
    transformRouteParams(route.query.category, "array") ?? [];

  if (filterCategories) {
    appliedFilterTypes = [
      ...getAppliedCategoryFilterTypes(filterCategories, routeCategories),
    ];
  }

  if (statusFilterType) {
    appliedFilterTypes.push(statusFilterType);
  }

  return appliedFilterTypes;
};

const onCloseFilterMenu = () => {
  sheetMenuIsOpen.value = false;

  filtersApplied({
    filters: getAppliedFilterTypes(),
    page_type: "searchEventsPage",
    sort:
      transformRouteParams(route.query.sort, "string") ??
      CategorySorting.Featured,
  });

  sheetMenuIsOpen.value = false;
};

const onClearFilters = () => {
  router.replace({
    ...route,
    query: {},
  });

  filtersCleared({ page_type: "searchEventsPage" });
  onCloseFilterMenu();
};

const filterCount = computed(() => {
  let categoriesCount = 0;
  const statusCount = route.query.eventStatus ? 1 : 0;

  if (route.query.category) {
    categoriesCount = Array.isArray(route.query.category)
      ? route.query.category.length
      : 1;
  }

  const sortingApplied = transformRouteParams(route.query.sort, "string")
    ? 1
    : 0;

  return categoriesCount + statusCount + sortingApplied || undefined;
});
</script>

<template>
  <BaseFilterButton
    v-bind="$attrs"
    data-test="event-filter-button"
    :filter-count="filterCount"
    page-type="searchProductsPage"
    @open-filter-menu="sheetMenuIsOpen = true"
  />
  <BaseSheetMenu
    :is-open="sheetMenuIsOpen"
    display-side="right"
    @close="onCloseFilterMenu"
  >
    <BaseMenuWithHeader
      :title="$t('filter.title')"
      :hide-back-button="true"
      data-test="event-filter-section"
      @close-menu="onCloseFilterMenu"
    >
      <BaseFilterSection
        unique-name="sort"
        type="single"
        :label="$t('sort.title')"
        :default-value="CategorySorting.Featured"
        :options="[
          {
            label: $t('sort.featured'),
            value: CategorySorting.Featured,
          },
          {
            label: translateSortLabel('closing', 'earliest', 'latest'),
            value: CategorySorting.ClosingTimeFirstToLast,
          },
          {
            label: translateSortLabel('closing', 'latest', 'earliest'),
            value: CategorySorting.ClosingTimeLastToFirst,
          },
        ]"
      />
      <BaseFilterSection
        unique-name="eventStatus"
        type="single"
        label="Status"
        default-value="all"
        :options="[
          {
            label: $t('filter.status.all'),
            value: 'all' as string,
          },
          {
            label: $t('filter.status.live'),
            value: EventStatus.Live,
          },
          {
            label: $t('filter.status.upcoming'),
            value: EventStatus.Upcoming,
          },
          {
            label: $t('filter.status.finished'),
            value: EventStatus.Finished,
          },
        ]"
      />
      <template v-if="filters?.categoryFilters">
        <BaseFilterSection
          v-for="(options, category) in filters.categoryFilters"
          :key="category"
          unique-name="category"
          type="multiple"
          :label="$t(`category.types.${category}`)"
          :options="options"
        />
      </template>

      <template #actions>
        <BaseFilterControlButtons
          :is-disabled="isLoading"
          @clear-filters="onClearFilters"
          @apply-filters="onCloseFilterMenu"
        />
      </template>
    </BaseMenuWithHeader>
  </BaseSheetMenu>
</template>
